import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, X, Search, Check, Sparkles } from "lucide-react";
import { API, errMsg, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const LOOK_TYPES = ["Makeup", "Outfit", "Jewellery", "Bag", "Full Look", "Other"];

export default function PostLook() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef();
  const [media, setMedia] = useState([]);
  const [caption, setCaption] = useState("");
  const [lookType, setLookType] = useState("Full Look");
  const [anonymous, setAnonymous] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    API.get("/products?limit=200").then((r) => setProducts(r.data.items)).catch(() => {});
  }, [user, loading, nav]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMedia([`${process.env.REACT_APP_BACKEND_URL}${data.url}`]); toast.success("Photo added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = (p) => setSelected((s) => s.find((x) => x.id === p.id) ? s.filter((x) => x.id !== p.id) : [...s, p]);
  const filtered = products.filter((p) => !search || p.title.toLowerCase().includes(search.toLowerCase())).slice(0, 24);

  const submit = async () => {
    if (!media.length) { toast.error("Add a photo of your look ✨"); return; }
    if (!caption.trim()) { toast.error("Add a caption"); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/community/posts", { type: "look", caption: `${lookType !== "Full Look" ? `[${lookType}] ` : ""}${caption}`, media, product_ids: selected.map((p) => p.id), anonymous });
      toast.success(data.message);
      nav("/sakhi-wall");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#FAF6F1] min-h-[80vh]" data-testid="post-look-page">
      <Seo title="Show Us Your Look | Suvikka Sakhi" noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">Suvikka Sakhi</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">Show Us Your Look ✨</h1>
        <p className="text-stone-500 mt-2">Share your style, tag what you're wearing, and let other Sakhis shop your look.</p>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-6 space-y-5">
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            {media[0] ? (
              <div className="relative rounded-2xl overflow-hidden">
                <img src={media[0]} alt="your look" className="w-full max-h-[420px] object-cover" />
                <button onClick={() => setMedia([])} className="absolute top-3 right-3 bg-black/60 text-white p-1.5 rounded-full" data-testid="look-remove-photo"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="look-upload" className="w-full border-2 border-dashed border-stone-300 rounded-2xl py-16 flex flex-col items-center gap-2 text-stone-400 hover:border-[#6A1B29] hover:text-[#6A1B29] transition-colors">
                <ImagePlus className="w-8 h-8" /> <span className="text-sm font-medium">Upload your look</span>
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2" data-testid="look-type">
            {LOOK_TYPES.map((t) => (
              <button key={t} onClick={() => setLookType(t)} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${lookType === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{t}</button>
            ))}
          </div>

          <textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption your look… ✨" className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#6A1B29]" data-testid="look-caption" />

          <div>
            <p className="text-sm font-semibold text-stone-900 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#6A1B29]" /> Tag products in your look</p>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="look-selected">
                {selected.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 bg-[#FFF0F4] text-[#501325] text-xs font-medium pl-1.5 pr-2 py-1 rounded-full">
                    <img src={p.images?.[0]} alt="" className="w-5 h-5 rounded-full object-cover" /> {p.title}
                    <button onClick={() => toggle(p)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative mt-3">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to tag" className="w-full border border-stone-300 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#6A1B29]" data-testid="look-product-search" />
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3 max-h-64 overflow-y-auto" data-testid="look-product-grid">
              {filtered.map((p) => {
                const sel = selected.find((x) => x.id === p.id);
                return (
                  <button key={p.id} onClick={() => toggle(p)} data-testid={`tag-product-${p.sku || p.id}`} className={`relative text-left border rounded-xl overflow-hidden ${sel ? "border-[#6A1B29] ring-1 ring-[#6A1B29]" : "border-stone-200"}`}>
                    <img src={p.images?.[0]} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                    {sel && <span className="absolute top-1 right-1 bg-[#6A1B29] text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
                    <span className="block text-[10px] text-stone-600 px-1.5 py-1 line-clamp-1">{p.title}</span>
                    <span className="block text-[10px] font-semibold text-stone-900 px-1.5 pb-1">{inr(p.selling_price)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="look-anonymous" /> Post anonymously (shown as "Anonymous Sakhi")</label>

          <button onClick={submit} disabled={busy} className="w-full bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold py-3.5 rounded-full disabled:opacity-60" data-testid="look-submit">{busy ? "Posting…" : "POST MY LOOK"}</button>
          <p className="text-xs text-stone-400 text-center">Looks are reviewed before appearing on the Sakhi Wall.</p>
        </div>
      </div>
    </div>
  );
}
